/**
 * ReceiptUploadCard — standalone upload affordance on the Belege page (unlike
 * `TransactionRow`'s `ReceiptUpload`, this upload has no `transactionId`: the
 * server stores the file unassigned and returns match `candidates`). Mirrors
 * the button-styled hidden-file-input pattern from `TransactionRow.tsx`.
 *
 * On success: an already-attached receipt (rare — only if a server-side
 * auto-match happened) surfaces the "Beleg zugeordnet" toast; a receipt with
 * candidates seeds the `receiptCandidates` query cache at the *default* key
 * (no amount filter) with the response's candidates and opens the picker
 * immediately, so `CandidatePicker` shows them without an extra round trip; a
 * receipt with none of the above just lands in the unassigned queue.
 */
import { useState, type ChangeEvent } from 'react';
import { Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useOnlineStatus } from '../../context/OnlineStatusContext.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { qk } from '../../lib/queryKeys.ts';
import { useUploadReceipt } from '../../lib/hooks/useReceipts.ts';
import type { Receipt } from '../../lib/types.ts';

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

export interface ReceiptUploadCardProps {
  /** Fired when the freshly uploaded receipt has candidates to review. */
  onOpenPicker: (receipt: Receipt) => void;
}

export function ReceiptUploadCard({ onOpenPicker }: ReceiptUploadCardProps) {
  const qc = useQueryClient();
  const { guardOnline } = useOnlineStatus();
  const { toast } = useToast();
  const upload = useUploadReceipt();
  const [error, setError] = useState<string | null>(null);

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;
    if (!guardOnline()) return;
    setError(null);
    upload.mutate(
      { file },
      {
        onSuccess: ({ receipt, candidates }) => {
          if (receipt.transaction_id !== null) {
            toast('Beleg zugeordnet', { variant: 'success' });
            return;
          }
          if (candidates.length > 0) {
            qc.setQueryData(qk.receiptCandidates(receipt.id, undefined), candidates);
            onOpenPicker(receipt);
            return;
          }
          toast('Beleg hochgeladen.', { variant: 'success' });
        },
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Fehler beim Hochladen.'),
      },
    );
  }

  return (
    <div className="space-y-1.5">
      <label
        className={`inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-sky-700 active:scale-[0.98] focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-sky-600 dark:bg-sky-500 dark:hover:bg-sky-400 dark:focus-within:outline-sky-500 sm:w-auto sm:px-6 ${
          upload.isPending ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
      >
        <Upload className="h-4 w-4" aria-hidden />
        {upload.isPending ? 'Lädt hoch …' : 'Beleg hochladen'}
        <input
          type="file"
          accept={ACCEPT}
          capture="environment"
          className="sr-only"
          onChange={onFileChange}
          disabled={upload.isPending}
        />
      </label>
      {error ? <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}
    </div>
  );
}
